/** @jsx isolateComponent */

import type { ReactNode } from 'react';

import { formatTimeSpan, formatDateTime } from '@zapier/date';
import { Colors, Icon, Link, Typography } from '@zapier/design-system';
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';

import { useUniversalNavContext } from '../../../../universal-nav/hooks';
import { TrialPlanTypes } from '../../../constants';

import UsageBar from './UsageBar';

export type Props = {
  /**
   * Content to render at bottom of section. Typically used to
   * render a CTA to upgrade plan or similar.
   */
  children?: ReactNode;
  /**
   * Whether or not the type of plan is metered
   */
  isPlanMetered?: boolean;
  /**
   * Text to display for the manage plan link.
   */
  managePlanLabel?: string;
  /**
   * URL to where the plan can be managed.
   */
  managePlanHref?: string;
  /**
   * Maximum number of free tasks for the usage period.
   */
  numFreeTasksMax?: number;
  /**
   * Number of tasks that have been used in the usage period.
   */
  numTasks: number;
  /**
   * Maximum number of tasks that can be used in the usage period.
   */
  numTasksMax: number;
  /**
   * Number of Zaps that are currently on.
   */
  numZaps: number;
  /**
   * Maximum number of Zaps that can be on.
   */
  numZapsMax: number;
  /**
   * String representation of when usage resets.
   */
  resetDate: string;
  /**
   * The title of the section within the sidebar.
   */
  title?: string;
  /**
   * Name of the trial plan the account is on, if it exists
   */
  trialPlanName?: TrialPlanTypes;
  /**
   * End date for the account's trial plan, if it exists
   */
  trialPlanEndDate?: string;
};

const Styles = labelStyles('UniversalSidebarUsage', {
  root: {
    display: 'grid',
    gap: 10,
    whiteSpace: 'nowrap',
    padding: '0 10px 10px',
  },
  header: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    gap: 20,
    alignItems: 'center',
    ...Typography.base,
    fontSize: 16,
    fontWeight: 600,
    color: Colors.gray80,
  },
  planName: {
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    ...Typography.paragraph3Medium,
    color: Colors.neutral700,
  },
  content: {
    margin: '0 5px',
  },
  usages: {
    display: 'grid',
    gap: 5,
    marginBottom: 5,
  },
  resetInfo: {
    display: 'grid',
    gap: 10,
    ...Typography.smallPrint1Medium,
    gridTemplateRows: '1fr auto',
    paddingTop: '10px',

    color: Colors.neutral700,
  },
  managePlanLink: {
    fontWeight: 600,
    textDecoration: 'underline',
    '&:hover, &:focus': {
      color: Colors.blue,
      outline: 'none',
    },
  },
});

type UsagesProps = Required<
  Pick<
    Props,
    | 'isPlanMetered'
    | 'numFreeTasksMax'
    | 'numTasks'
    | 'numTasksMax'
    | 'numZaps'
    | 'numZapsMax'
  >
>;

const Usages = (props: UsagesProps) => {
  const hasUsedExtraTasks =
    props.isPlanMetered && props.numTasks > props.numFreeTasksMax;
  return (
    <div css={Styles.usages}>
      <UsageBar
        label={props.isPlanMetered ? 'Included Tasks' : 'Tasks'}
        max={props.isPlanMetered ? props.numFreeTasksMax : props.numTasksMax}
        value={
          props.isPlanMetered
            ? Math.min(props.numTasks, props.numFreeTasksMax)
            : props.numTasks
        }
      />
      {hasUsedExtraTasks && (
        <UsageBar
          label="Extra Tasks"
          max={props.numTasksMax - props.numFreeTasksMax}
          value={props.numTasks - props.numFreeTasksMax}
        />
      )}
      <UsageBar label="Zaps" max={props.numZapsMax} value={props.numZaps} />
    </div>
  );
};

/**
 * @private
 * Renders account usage data within the `UniversalSidebar`.
 */
const UniversalSidebarUsage = ({
  numTasks,
  numTasksMax,
  numZaps,
  numZapsMax,
  resetDate,
  children = undefined,
  trialPlanName = undefined,
  trialPlanEndDate = undefined,

  isPlanMetered = false,
  numFreeTasksMax = 0,
  managePlanHref = '/app/settings/billing',
  managePlanLabel = 'Manage plan',
  title = 'Usage',
}: Props) => {
  const { getFullyQualifiedURL } = useUniversalNavContext();
  const relativeDate = formatTimeSpan(Date.now(), resetDate, false);

  return (
    <section css={Styles.root}>
      <div css={Styles.header}>
        <Icon name="miscWallet" size={30} />
        <h3 css={Styles.planName}>
          {trialPlanName ? `${trialPlanName} plan (Trial)` : title}
        </h3>
      </div>
      <div css={Styles.content}>
        <Usages
          {...{
            isPlanMetered,
            numFreeTasksMax,
            numTasks,
            numTasksMax,
            numZaps,
            numZapsMax,
          }}
        />

        <p css={Styles.resetInfo}>
          <span>
            {trialPlanName && trialPlanEndDate
              ? `Trial ends on ${formatDateTime(trialPlanEndDate, 'default')}`
              : `Monthly usage resets in ${relativeDate}`}
          </span>
          <Link href={getFullyQualifiedURL(managePlanHref)}>
            {managePlanLabel}
          </Link>
        </p>
      </div>
      {children}
    </section>
  );
};

export default UniversalSidebarUsage;
